import React, { useState } from 'react';
import { CheckCircle2Icon, AlertCircleIcon, PhoneIcon, SendIcon } from 'lucide-react';
import { WhatsAppIcon } from './icons/WhatsAppIcon';
import { useLanguage } from '../contexts/LanguageContext';
import { services } from '../data/services';
import { business } from '../data/site';
import { formLabels } from '../data/ui';
import { buttonClass } from '../utils/styles';
type Status = 'idle' | 'submitting' | 'success';
interface FormState {
  name: string;
  phone: string;
  email: string;
  service: string;
  address: string;
  date: string;
  message: string;
}
const emptyForm: FormState = {
  name: '',
  phone: '',
  email: '',
  service: '',
  address: '',
  date: '',
  message: ''
};
const fieldClass = 'w-full rounded-md border bg-white px-3.5 py-3 text-[0.95rem] text-ink placeholder:text-ink/40 transition-colors duration-150 ease-out-expo';
export function LeadForm() {
  const {
    t
  } = useLanguage();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [status, setStatus] = useState<Status>('idle');
  function update(key: keyof FormState, value: string) {
    setForm(current => ({
      ...current,
      [key]: value
    }));
    setErrors(current => ({
      ...current,
      [key]: undefined
    }));
  }
  function validate(): boolean {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) next.name = t(formLabels.errorRequired);
    if (!form.phone.trim()) next.phone = t(formLabels.errorRequired);else if (form.phone.replace(/\D/g, '').length < 10) next.phone = t(formLabels.errorPhone);
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) {
      next.email = t(formLabels.errorEmail);
    }
    if (!form.service) next.service = t(formLabels.errorRequired);
    setErrors(next);
    return Object.keys(next).length === 0;
  }
  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!validate()) return;
    setStatus('submitting');
    window.setTimeout(() => setStatus('success'), 700);
  }
  if (status === 'success') {
    return <div className="rounded-xl border border-gold/40 bg-gold-50 p-8 text-center" data-id="element-247" data-mp="2qusqv4v98g06">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-navy" data-id="element-248" data-mp="2lvzddcfywp00">
          <CheckCircle2Icon className="h-6 w-6 text-gold" aria-hidden="true" data-id="element-249" data-mp="2hvxltw3y5byg" />
        </span>
        <h3 className="mt-4 font-display text-2xl text-navy" data-id="element-250" data-mp="2cyp05ljlapsg">{t(formLabels.successTitle)}</h3>
        <p className="mx-auto mt-3 max-w-md text-ink/75" data-id="element-251" data-mp="284t7mym3hv4o">{t(formLabels.successBody)}</p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3" data-id="element-252" data-mp="238cgrwnih2nw">
          <a href={business.phoneHref} className={buttonClass('navy', 'md')} data-id="element-253" data-mp="1y8zlqomhkpa8">
            <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-254" data-mp="1u6d2hc3vscon" />
            {business.phoneDisplay}
          </a>
          <button type="button" onClick={() => {
          setForm(emptyForm);
          setStatus('idle');
        }} className={buttonClass('outline', 'md')} data-id="element-255" data-mp="1p705vzn7jr8v">
            {t(formLabels.sendAnother)}
          </button>
        </div>
      </div>;
  }
  const hasErrors = Object.values(errors).some(Boolean);
  return <form onSubmit={handleSubmit} noValidate className="rounded-xl border border-navy/10 bg-white p-6 shadow-card sm:p-8" data-id="element-256" data-mp="ipa3kxo2hmy1">
      <h3 className="font-display text-2xl text-navy" data-id="element-257" data-mp="3i5ovyjppezxr">{t(formLabels.heading)}</h3>
      <p className="mt-2 text-sm text-ink/70" data-id="element-258" data-mp="3m57j7m4zzn3r">{t(formLabels.subheading)}</p>

      {hasErrors ? <p role="alert" className="mt-5 flex items-start gap-2 rounded-md bg-red-50 px-3.5 py-3 text-sm font-medium text-red-800" data-id="element-259" data-mp="3r4ts5jdsmh6r">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-260" data-mp="3vxmw451ftlhs" />
          {t(formLabels.errorSummary)}
        </p> : null}

      <div className="mt-6 grid gap-5 sm:grid-cols-2" data-id="element-261" data-mp="33eg2u0mlewfv">
        <Field id="lead-name" label={t(formLabels.name)} required error={errors.name} value={form.name} onChange={value => update('name', value)} autoComplete="name" data-id="element-262" data-mp="19ws736hog1p8" />
        <Field id="lead-phone" label={t(formLabels.phone)} required type="tel" error={errors.phone} value={form.phone} onChange={value => update('phone', value)} placeholder="(214) 000-0000" autoComplete="tel" data-id="element-263" data-mp="1ipvqdvf078rs" />
        <Field id="lead-email" label={t(formLabels.email)} type="email" error={errors.email} value={form.email} onChange={value => update('email', value)} autoComplete="email" data-id="element-264" data-mp="1nnngql002mtv" />
        <div data-id="element-265" data-mp="79dupxj8da67">
          <label htmlFor="lead-service" className="block text-sm font-semibold text-navy" data-id="element-266" data-mp="7aypno9giv4l">
            {t(formLabels.service)} <span className="text-gold-dark" data-id="element-267" data-mp="39n4pw1j2y2q">*</span>
          </label>
          <select id="lead-service" value={form.service} onChange={event => update('service', event.target.value)} aria-invalid={Boolean(errors.service)} aria-describedby={errors.service ? 'lead-service-error' : undefined} className={`${fieldClass} mt-2 ${errors.service ? 'border-red-400' : 'border-navy/20 focus:border-navy'}`} data-id="element-268" data-mp="3p9j69bz3o2y1">
            <option value="" data-id="element-269" data-mp="35kh5dvf1exdr">{t(formLabels.servicePlaceholder)}</option>
            {services.map(service => <option key={service.slug} value={service.slug} data-id="element-270" data-mp="3aiinoe4n2so6">
                {t(service.name)}
              </option>)}
            <option value="handyman" data-id="element-271" data-mp="31jouyyufimfw">
              {t({
              en: 'Handyman / other repairs',
              es: 'Handyman / otras reparaciones'
            })}
            </option>
          </select>
          {errors.service ? <p id="lead-service-error" className="mt-1.5 text-sm text-red-700" data-id="element-272" data-mp="ztdcfaexgngv">
              {errors.service}
            </p> : null}
        </div>
        <Field id="lead-address" label={t(formLabels.address)} value={form.address} onChange={value => update('address', value)} autoComplete="street-address" placeholder="Dallas, TX" data-id="element-273" data-mp="xc2jdxo6hbm4" />
        <Field id="lead-date" label={t(formLabels.preferredDate)} type="date" value={form.date} onChange={value => update('date', value)} data-id="element-274" data-mp="jffkhpco0bdk" />
        <div className="sm:col-span-2" data-id="element-275" data-mp="1c44g58uybohx">
          <label htmlFor="lead-message" className="block text-sm font-semibold text-navy" data-id="element-276" data-mp="1c560k3xuxncb">
            {t(formLabels.message)}
          </label>
          <textarea id="lead-message" rows={4} value={form.message} onChange={event => update('message', event.target.value)} placeholder={t(formLabels.messagePlaceholder)} className={`${fieldClass} mt-2 resize-y border-navy/20 focus:border-navy`} data-id="element-277" data-mp="12e1obbzkggl6" />
        </div>
      </div>

      <button type="submit" disabled={status === 'submitting'} className={buttonClass('gold', 'lg', 'mt-6 w-full')} data-id="element-278" data-mp="22fg7qhvqzbjc">
        <SendIcon className="h-4 w-4" aria-hidden="true" data-id="element-279" data-mp="2ca85pmt8s0en" />
        {status === 'submitting' ? t(formLabels.submitting) : t(formLabels.submit)}
      </button>

      <div className="mt-5 flex flex-col items-center gap-3 border-t border-navy/10 pt-5 sm:flex-row sm:justify-between" data-id="element-280" data-mp="2lbxxitr1d1cy">
        <p className="text-sm text-ink/70" data-id="element-281" data-mp="2qaivsnmq9ayr">{t(formLabels.prefer)}</p>
        <div className="flex items-center gap-2" data-id="element-282" data-mp="2q8ocfdr3tqws">
          <a href={business.phoneHref} className={buttonClass('outline', 'sm')} data-id="element-283" data-mp="2v2smw6zkwh2m">
            <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-284" data-mp="3013u09yhodh5" />
            {business.phoneDisplay}
          </a>
          <a href={business.whatsappHref} target="_blank" rel="noreferrer" className={buttonClass('whatsapp', 'sm')} data-id="element-285" data-mp="350yjc92m8v03">
            <WhatsAppIcon className="h-4 w-4" data-id="element-286" data-mp="3ubxsan5nnl2p" />
            WhatsApp
          </a>
        </div>
      </div>
    </form>;
}
interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
  error?: string;
  placeholder?: string;
  autoComplete?: string;
}
function Field({
  id,
  label,
  value,
  onChange,
  type = 'text',
  required = false,
  error,
  placeholder,
  autoComplete
}: FieldProps) {
  return <div data-id="element-287" data-mp="36n69gmdygf2l">
      <label htmlFor={id} className="block text-sm font-semibold text-navy" data-id="element-288" data-mp="36nej85es749k">
        {label} {required ? <span className="text-gold-dark" data-id="element-289" data-mp="3lcas24onwlb8">*</span> : null}
      </label>
      <input id={id} type={type} value={value} required={required} placeholder={placeholder} autoComplete={autoComplete} onChange={event => onChange(event.target.value)} aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-error` : undefined} className={`${fieldClass} mt-2 ${error ? 'border-red-400' : 'border-navy/20 focus:border-navy'}`} data-id="element-290" data-mp="3ldlwm6pxlx3e" />
      {error ? <p id={`${id}-error`} className="mt-1.5 text-sm text-red-700" data-id="element-291" data-mp="186dus9wbc88f">
          {error}
        </p> : null}
    </div>;
}